import type { PathAnchor } from '../types';

export interface PenDrawingState {
  isActive: boolean;
  anchors: PathAnchor[];
  isDragging: boolean;
  dragAnchorIndex: number;
  dragStartX: number;
  dragStartY: number;
}

export function createPenState(): PenDrawingState {
  return {
    isActive: false,
    anchors: [],
    isDragging: false,
    dragAnchorIndex: -1,
    dragStartX: 0,
    dragStartY: 0,
  };
}

export function penStart(state: PenDrawingState, x: number, y: number): void {
  state.isActive = true;
  state.anchors = [{ x, y }];
  state.isDragging = true;
  state.dragAnchorIndex = 0;
  state.dragStartX = x;
  state.dragStartY = y;
}

export function penAddAnchor(state: PenDrawingState, x: number, y: number): void {
  state.anchors.push({ x, y });
  state.isDragging = true;
  state.dragAnchorIndex = state.anchors.length - 1;
  state.dragStartX = x;
  state.dragStartY = y;
}

export function penDrag(state: PenDrawingState, x: number, y: number): void {
  if (!state.isDragging || state.dragAnchorIndex < 0) return;
  const anchor = state.anchors[state.dragAnchorIndex];
  if (!anchor) return;

  const dx = x - anchor.x;
  const dy = y - anchor.y;

  anchor.handleOut = { x: anchor.x + dx, y: anchor.y + dy };
  anchor.handleIn = { x: anchor.x - dx, y: anchor.y - dy };
}

export function penMouseUp(state: PenDrawingState): void {
  state.isDragging = false;
  state.dragAnchorIndex = -1;
}

export function penFinalize(state: PenDrawingState, closed: boolean): PathAnchor[] | null {
  if (state.anchors.length < 2) {
    state.isActive = false;
    state.anchors = [];
    state.isDragging = false;
    state.dragAnchorIndex = -1;
    return null;
  }

  const result = state.anchors.map((a) => ({
    x: a.x,
    y: a.y,
    handleIn: a.handleIn ? { ...a.handleIn } : undefined,
    handleOut: a.handleOut ? { ...a.handleOut } : undefined,
  }));

  state.isActive = false;
  state.anchors = [];
  state.isDragging = false;
  state.dragAnchorIndex = -1;

  return result;
}

export function penRemoveLastAnchor(state: PenDrawingState): void {
  if (state.anchors.length > 1) {
    state.anchors.pop();
  }
}

export function isNearFirstAnchor(state: PenDrawingState, x: number, y: number, threshold = 10): boolean {
  if (state.anchors.length < 3) return false;
  const first = state.anchors[0];
  const dx = x - first.x;
  const dy = y - first.y;
  return Math.sqrt(dx * dx + dy * dy) <= threshold;
}
